import client from '../../../config/api';
import {
  CLEAR_DIRTY_FIELDS,
  DELETE_REPAIR_ASSESSMENT_ROW,
  LOCAL_SET_CURRENT_JOB,
  UPSERT_REPAIR_ASSESSMENT_ROW,
} from '../mutations/mutationTypes';
import { SEND_WEBSOCKET_MESSAGE } from '../../websocket/actions/actionTypes';

import { GET_WEBSOCKET_CONNECTION_ID } from '../../websocket/getters/getterTypes';

import { ITEM_TO_EXCLUDE } from '../../reports/mutations/mutationTypes';
import { CreateLatestDebouncedAction } from '../../../helpers/CreateLatestDebouncedAction';
import {
  ENTITY_TYPES,
  attachBase,
  setSnapshot,
} from '../../../helpers/conflictTracker';
import {
  selectUnchangedForceFields,
  snapshotRowFields,
} from '../../../helpers/repairAssessmentDirtyMerge';

const ratRowWriteTail = new Map();

function queueRatRowWrite(rowId, work) {
  const previous = ratRowWriteTail.get(rowId) || Promise.resolve();
  const next = previous.catch(() => {}).then(work);
  ratRowWriteTail.set(rowId, next);
  return next.finally(() => {
    if (ratRowWriteTail.get(rowId) === next) {
      ratRowWriteTail.delete(rowId);
    }
  });
}

async function putRepairAssessmentRow({
  job,
  row,
  tool,
  clientWebSocketId,
  silentConflict,
}) {
  const requestBody = attachBase(ENTITY_TYPES.RAT_ROW, row);
  const { data } = await client.put(
    `/job/${job._id}/ra/${row.id}`,
    requestBody,
    {
      params: { tool, wsConnectionId: clientWebSocketId },
      silentConflict,
    }
  );
  setSnapshot(ENTITY_TYPES.RAT_ROW, data);
  return data;
}

export default {
  async JOBS_GET_FOLDERS(_1, jobId) {
    try {
      return await client.get(`/job/${jobId}/folders`);
    } catch (error) {
      throw new Error(error);
    }
  },

  GET_JOB_ATTACHMENTS_COUNT: async (_1, jobId) => {
    try {
      const response = await client.get(`/job/${jobId}/attachments/count`);
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  },

  UPDATE_JOB_REPORT: CreateLatestDebouncedAction({
    keyExtractor: (job) => job._id,
    delay: 1000,
    /* eslint-disable no-unused-vars */
    handlerFn: async ({ context, payload }) => {
      const res = await client.put(`/job/${payload._id}`, payload);
      return res.data;
    },
    commitFn: (commit, job) => {
      console.log('Committing job update', job);
      commit(LOCAL_SET_CURRENT_JOB, job);
    },
  }),
  UPDATE_JOB: CreateLatestDebouncedAction({
    keyExtractor: (job) => job._id,
    delay: 500,
    /* eslint-disable no-unused-vars */
    handlerFn: async ({ context, payload }) => {
      const res = await client.put(`/job/${payload._id}`, payload);

      return res.data;
    },
    commitFn: (commit, job) => {
      commit(LOCAL_SET_CURRENT_JOB, job);
    },
  }),

  async GET_JOB({ commit, dispatch }, id) {
    const result = await client.get(`/job/${id}`);

    commit(LOCAL_SET_CURRENT_JOB, result.data);

    if (result.data && result.data._id) {
      await dispatch(
        SEND_WEBSOCKET_MESSAGE,
        {
          action: 'jobJoin',
          data: { _id: result.data._id },
        },
        { root: true }
      );
    }

    if (result.data.excludedItems) {
      result.data.excludedItems.forEach((item) => {
        commit(ITEM_TO_EXCLUDE, { id: item, value: true }, { root: true });
      });
    }
  },

  async GET_JOB_PROGRESS({ commit }, id) {
    const result = await client.get(`/job/${id}/progress`);
    commit('SET_JOB_PROGRESS_TASKS', result.data.tableData);
  },

  async ADD_REPAIR_ASSESSMENT_ROW(
    { commit, rootGetters },
    { job, rows, tool, replace = false, replaceMinor = false }
  ) {
    commit('SET_WAITING_FOR_WRITE', true, { root: true });

    try {
      const clientWebSocketId = rootGetters[GET_WEBSOCKET_CONNECTION_ID];
      const result = await client.post(
        `/job/${job._id}/ra/`,
        { rows, replace, replaceMinor },
        {
          params: { tool, wsConnectionId: clientWebSocketId },
        }
      );

      const addedRow = result.data;

      commit(
        UPSERT_REPAIR_ASSESSMENT_ROW,
        { incoming: addedRow },
        { root: true }
      );

      return addedRow;
    } finally {
      commit('SET_WAITING_FOR_WRITE', false, { root: true });
    }
  },
  UPDATE_REPAIR_ASSESSMENT_ROW: CreateLatestDebouncedAction({
    keyExtractor: (p) => p.row._id || p.row.id,
    delay: 0,
    handlerFn: async ({ context, payload }) => {
      const { job, row, tool, forceFields } = payload;
      const rowId = row._id || row.id;

      return queueRatRowWrite(rowId, async () => {
        context.commit('SET_WAITING_FOR_WRITE', true, { root: true });

        const dirtyMap = context.state.editingFieldsByRow?.[row.id] || {};
        const candidateFields = Array.from(
          new Set(
            [...(forceFields || []), ...Object.keys(dirtyMap)].filter(Boolean)
          )
        );
        const sentValues = snapshotRowFields(row, candidateFields);

        const { rootGetters } = context;
        const clientWebSocketId = rootGetters[GET_WEBSOCKET_CONNECTION_ID];

        let data;
        try {
          data = await putRepairAssessmentRow({
            job,
            row,
            tool,
            clientWebSocketId,
            silentConflict: true,
          });
        } catch (error) {
          if (error.status !== 409 || !error.persistedData) {
            throw error;
          }
          setSnapshot(ENTITY_TYPES.RAT_ROW, error.persistedData);
          data = await putRepairAssessmentRow({
            job,
            row,
            tool,
            clientWebSocketId,
            silentConflict: false,
          });
        }

        const liveRow =
          context.state.currentJob?.repairAssessment?.find(
            (item) => item.id === row.id
          ) || row;
        const safeForceFields = selectUnchangedForceFields(
          sentValues,
          liveRow,
          candidateFields
        );

        return { row: data, forceFields: safeForceFields, rowId: row.id };
      });
    },
    commitFn: (commit, { row, forceFields, rowId }) => {
      commit('SET_WAITING_FOR_WRITE', false, { root: true });
      commit(
        UPSERT_REPAIR_ASSESSMENT_ROW,
        { incoming: row, forceFields },
        { root: true }
      );
      if (forceFields?.length) {
        commit(
          CLEAR_DIRTY_FIELDS,
          { rowId: rowId || row.id, fields: forceFields },
          { root: true }
        );
      }
    },
    errorFn: (context) => {
      context.commit('SET_WAITING_FOR_WRITE', false, { root: true });
    },
  }),

  DELETE_REPAIR_ASSESSMENT_ROW: async (
    { commit, rootGetters },
    { job, row, tool }
  ) => {
    commit('SET_WAITING_FOR_WRITE', true, { root: true });

    try {
      const clientWebSocketId = rootGetters[GET_WEBSOCKET_CONNECTION_ID];
      const result = await client.delete(`/job/${job._id}/ra/${row.id}`, {
        params: { tool, wsConnectionId: clientWebSocketId },
      });

      commit(DELETE_REPAIR_ASSESSMENT_ROW, row.id, { root: true });

      return result.data;
    } finally {
      commit('SET_WAITING_FOR_WRITE', false, { root: true });
    }
  },

  async LINK_REPAIR_ASSESSMENT_TASKS(
    { commit, rootGetters, rootState },
    { job, row, taskIds, tool }
  ) {
    commit('SET_WAITING_FOR_WRITE', true, { root: true });

    try {
      const clientWebSocketId = rootGetters[GET_WEBSOCKET_CONNECTION_ID];
      const result = await client.put(
        `/job/${job._id}/ra/${row.id}/linked-tasks`,
        { taskIds },
        {
          params: { tool, wsConnectionId: clientWebSocketId },
        }
      );

      commit(
        UPSERT_REPAIR_ASSESSMENT_ROW,
        { incoming: result.data, forceFields: ['linkedTaskIds'] },
        { root: true }
      );

      const previousIds = new Set((row.linkedTaskIds || []).map(String));
      const nextIds = new Set((taskIds || []).map(String));
      const rowId = String(row.id);

      const patchTaskList = (list = []) =>
        list.map((task) => {
          const taskId = String(task._id);
          const currentlyLinked = previousIds.has(taskId);
          const shouldLink = nextIds.has(taskId);
          if (currentlyLinked === shouldLink) {
            return task;
          }

          const linkedRaIds = (task.linkedRaIds || [])
            .map(String)
            .filter((id) => id !== rowId);
          if (shouldLink) {
            linkedRaIds.push(rowId);
          }
          return { ...task, linkedRaIds };
        });

      if (rootState.jobTasks?.length) {
        commit('SET_JOB_TASKS', patchTaskList(rootState.jobTasks), {
          root: true,
        });
      }
      if (rootState.tasks?.length) {
        commit('SET_TASKS', patchTaskList(rootState.tasks), { root: true });
      }

      return result.data;
    } finally {
      commit('SET_WAITING_FOR_WRITE', false, { root: true });
    }
  },

  REPAIR_SELECT_REQUEST: CreateLatestDebouncedAction({
    keyExtractor: (job) => job._id,
    delay: 1000,
    handlerFn: async ({ context, payload }) => {
      const { _id } = payload;
      const res = await client.patch(
        `/jobs/${_id}/repairSelectRequest`,
        payload
      );
      return res.data;
    },
    commitFn: () => {},
  }),
};
